import React, { useState } from 'react';
import styled from 'styled-components';
import { COLORS } from '../../styles/Colors';

const InfoTitle = styled.h3`
  color: rgb(255, 199, 67); 
  font-size: 18px;
  margin: 0 0 15px 0;
  font-weight: bold;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 16px;
    margin: 0 0 10px 0;
    text-align: left;
  }
`;

const InfoText = styled.p`
  font-size: 15px;
  line-height: 1.5;
  margin: 0 0 12px 0;
  color: ${COLORS.text};
  
  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 1.4;
    margin: 0 0 10px 0;
  }
`;

const InfoContainer = styled.div`
  position: absolute;
  /* Desktop: centrato e più grande */
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  padding: 25px 30px;
  width: 480px;
  max-width: 90vw;
  z-index: 1000;
  display: ${props => props.$isVisible ? 'block' : 'none'};
  border: 2px solid rgba(255, 199, 67, 0.3);
  
  /* Animazione di entrata per desktop */
  animation: ${props => props.$isVisible ? 'fadeInScale' : 'fadeOutScale'} 0.3s ease-out;
  
  @media (max-width: 768px) {
    /* Mobile: torna al comportamento originale */
    top: 0;
    left: 0;
    right: 0;
    transform: none;
    width: 100%;
    max-width: none;
    border-radius: 0 0 12px 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    z-index: 1200;
    padding: calc(env(safe-area-inset-top, 0px) + 20px) 20px 20px 20px;
    backdrop-filter: blur(10px);
    background: rgba(255, 255, 255, 0.98);
    border: none;
    
    animation: ${props => props.$isVisible ? 'slideDown' : 'slideUp'} 0.3s ease-out;
  }
  
  /* Animazioni Desktop */
  @keyframes fadeInScale {
    from {
      transform: translate(-50%, -50%) scale(0.8);
      opacity: 0;
    }
    to {
      transform: translate(-50%, -50%) scale(1);
      opacity: 1;
    }
  }
  
  @keyframes fadeOutScale {
    from {
      transform: translate(-50%, -50%) scale(1);
      opacity: 1;
    }
    to {
      transform: translate(-50%, -50%) scale(0.8);
      opacity: 0;
    }
  }
  
  /* Animazioni Mobile */
  @keyframes slideDown {
    from {
      transform: translateY(-100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  
  @keyframes slideUp {
    from {
      transform: translateY(0);
      opacity: 1;
    }
    to {
      transform: translateY(-100%);
      opacity: 0;
    }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(0, 0, 0, 0.1);
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #666;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.15);
    color: #333;
    transform: scale(1.1);
  }
  
  @media (max-width: 768px) {
    top: calc(env(safe-area-inset-top, 0px) + 8px);
    right: 15px;
    font-size: 18px;
    width: 30px;
    height: 30px;
  }
`;

const CloseBottomButton = styled.button`
  width: 100%;
  background: rgb(255, 95, 67);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  margin-top: 20px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(255, 95, 67, 0.3);
  
  &:hover {
    background: rgb(235, 85, 57);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(255, 95, 67, 0.4);
  }
  
  @media (max-width: 768px) {
    margin-top: 15px;
    padding: 12px 16px;
  }
`;

const InfoHighlight = styled.span`
  color: rgb(255, 95, 67); 
  font-weight: bold;
`;

const WarnHighlight = styled.span`
  color: rgb(255, 183, 14); 
  font-weight: bold;
`;

const LocationButtonExample = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: ${COLORS.primary || '#f39c12'};
  color: white;
  border-radius: 50%;
  font-size: 14px;
  margin: 0 6px;
  vertical-align: middle;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  
  @media (max-width: 768px) {
    width: 24px;
    height: 24px;
    font-size: 12px;
    margin: 0 4px;
  }
`;

// Overlay per entrambe le versioni (più scuro per desktop)
const Overlay = styled.div`
  display: ${props => props.$isVisible ? 'block' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5); /* Più scuro per desktop */
  z-index: 999; /* Appena sotto l'InfoBox */
  
  @media (max-width: 768px) {
    background: rgba(0, 0, 0, 0.3); /* Più leggero per mobile */
    z-index: 1199;
  }
`;

// Contenitore per centrare meglio il contenuto
const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  
  @media (max-width: 768px) {
    gap: 10px;
  }
`;

// Sezione per raggruppare le informazioni
const InfoSection = styled.div`
  padding: 15px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  
  &:last-of-type {
    border-bottom: none;
    padding-bottom: 0;
  }
  
  &:first-of-type {
    padding-top: 0;
  }
  
  @media (max-width: 768px) {
    padding: 10px 0;
  }
`;

const InfoBox = ({ isMobile }) => {
  const [isVisible, setIsVisible] = useState(true);
  
  const handleClose = () => {
    setIsVisible(false);
  };
  
  if (!isVisible) {
    return null;
  }

  return (
    <>
      {/* Overlay per bloccare interazioni */}
      <Overlay $isVisible={isVisible} onClick={handleClose} />
      
      <InfoContainer $isVisible={isVisible}>
        <CloseButton onClick={handleClose}>✕</CloseButton>
        
        <ContentWrapper>
          <InfoTitle>🗺️ Guida alla Mappa Eventi Liguria</InfoTitle>
          
          <InfoSection>
            <InfoText>
              Con Finday puoi scoprire tra <InfoHighlight>centinaia di eventi</InfoHighlight> divisi per zona e categoria in tutta la Liguria
            </InfoText>
          </InfoSection>
          
          <InfoSection>
            <InfoText>
              <InfoHighlight>💡 Usa i filtri</InfoHighlight> per trovare eventi specifici per categoria, data e distanza
            </InfoText>
            
            <InfoText>
              Clicca sul pulsante <LocationButtonExample>
                <i className="fas fa-location-arrow"></i>
              </LocationButtonExample> per <InfoHighlight>definire la tua posizione</InfoHighlight> e vedere gli eventi più vicini a te
            </InfoText>
          </InfoSection>
          
          <InfoSection>
            <InfoText>
              <br />
              <WarnHighlight>Utilizza sempre il pulsante "INDICAZIONI STRADALI"</WarnHighlight> presente nel dettaglio dell'evento per la navigazione precisa
            </InfoText>
          </InfoSection>
        </ContentWrapper>
        
        <CloseBottomButton onClick={handleClose}>
          🚀 INIZIA A ESPLORARE
        </CloseBottomButton>
      </InfoContainer>
    </>
  );
};

export default InfoBox;
